import { useSelector } from 'react-redux';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useToasts } from 'react-toast-notifications';
import { Grid } from '@mui/material';
import {
  selectMedicalInfoAnswers,
  updateEmergencyContact,
} from '../../../redux/assessment';
import { selectPresentMeta } from '../../../redux/meta';
import { PatientEmergencyContactInput } from '../../../generated/graphql';
import { useAppDispatch } from '../../../pages/assessment';
import {
  AssessmentButtons,
  AssessmentCard,
  ControlledMaskedField,
  ControlledSelectField,
  ControlledTextField,
} from '../shared';
import { rules } from '../demographics/rules';

const EMERGENCY_CONTACT_RELATION = [
  'Care Giver',
  'Child',
  'Friend',
  'Guardian',
  'None',
  'Other',
  'Parent',
  'Self',
  'Sibling',
  'Spouse',
];

type EmergencyContactFormValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  relation: string;
  phone: string;
};

const EmergencyContactForm = () => {
  const dispatch = useAppDispatch();
  const meta = useSelector(selectPresentMeta);
  const medicalInfoAnswers = useSelector(selectMedicalInfoAnswers);

  const emergencyContact = medicalInfoAnswers
    ? medicalInfoAnswers[meta.screen] || {}
    : {};

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<EmergencyContactFormValues>({
    mode: 'onBlur',
  });

  const { addToast } = useToasts();

  const onSubmit: SubmitHandler<EmergencyContactFormValues> = async data => {
    try {
      const formattedPhone = data.phone.replace(/[^0-9]/g, '');
      const dataToSend: PatientEmergencyContactInput = {
        ...data,
        phone: formattedPhone,
      };

      await dispatch(updateEmergencyContact(dataToSend));
    } catch (e) {
      addToast(
        e.message ||
          'Sorry, something went wrong. Please refresh the page and try again.',
        { appearance: 'error' }
      );
    }
  };

  const relationOptions = EMERGENCY_CONTACT_RELATION.map(relation => ({
    label: relation,
    value: relation,
  }));

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AssessmentCard>
        <Grid container columnSpacing={1} rowSpacing={2}>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<EmergencyContactFormValues>
              control={control}
              name="firstName"
              defaultValue={emergencyContact?.firstName ?? ''}
              autoComplete="off"
              label="First name"
              rules={rules.firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<EmergencyContactFormValues>
              control={control}
              name="middleName"
              defaultValue={emergencyContact?.middleName ?? ''}
              autoComplete="off"
              label="Middle name (optional)"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledTextField<EmergencyContactFormValues>
              control={control}
              name="lastName"
              autoComplete="off"
              defaultValue={emergencyContact?.lastName ?? ''}
              label="Last name"
              rules={rules.lastName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledSelectField<EmergencyContactFormValues>
              control={control}
              name="relation"
              defaultValue={emergencyContact?.relation ?? undefined}
              label="Relation"
              options={relationOptions}
              rules={rules.relationToPatient}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <ControlledMaskedField<EmergencyContactFormValues>
              control={control}
              name="phone"
              defaultValue={emergencyContact?.phone ?? ''}
              label="Phone"
              mask="(999) 999-9999"
              rules={rules.phone}
            />
          </Grid>
        </Grid>
      </AssessmentCard>
      <AssessmentButtons
        primary={{
          children: 'Continue',
          type: 'submit',
          disabled: !isValid || isSubmitting,
          loading: isSubmitting,
        }}
      />
    </form>
  );
};

export default EmergencyContactForm;
